/**
 * ============================================================================
 * MODAL PROVIDER
 * ============================================================================
 * 
 * Provides global modal system
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useUIStore } from '../store';
import { useClickOutside } from '../hooks/useClickOutside';

const ModalContext = createContext(null);

/**
 * Modal Component
 */
const Modal = ({ name, isOpen, onClose, children, size = 'md' }) => {
  const modalRef = useClickOutside(isOpen ? onClose : null, isOpen);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${sizeClasses[size]} transform transition-all`}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Generic Modal Component
 */
const GenericModal = () => {
  const { modals, closeGenericModal } = useUIStore();
  const { open, component: Component, props } = modals.generic || {};

  if (!open || !Component) return null;

  return (
    <Modal isOpen={open} onClose={closeGenericModal} size={props?.size || 'md'}>
      <Component {...props} onClose={closeGenericModal} />
    </Modal>
  );
};

/**
 * Modal Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {Object} props.modals - Custom modal components
 * @returns {JSX.Element}
 */
export const ModalProvider = ({ children, modals: customModals = {} }) => {
  const { modals, openModal, closeModal, closeAllModals, openGenericModal, closeGenericModal } = useUIStore();

  const open = useCallback(
    (name, props = {}) => {
      openModal(name, props);
    },
    [openModal]
  );

  const close = useCallback(
    (name) => {
      closeModal(name);
    },
    [closeModal]
  );

  const value = {
    open,
    close,
    closeAll: closeAllModals,
    openGeneric: openGenericModal,
    closeGeneric: closeGenericModal,
    isOpen: (name) => modals[name] === true,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      {/* Render custom modals */}
      {Object.entries(customModals).map(([name, Component]) => (
        <Modal
          key={name}
          name={name}
          isOpen={modals[name] === true}
          onClose={() => close(name)}
          size={modals[`${name}Props`]?.size || 'md'}
        >
          <Component {...(modals[`${name}Props`] || {})} onClose={() => close(name)} />
        </Modal>
      ))}
      {/* Generic modal */}
      <GenericModal />
    </ModalContext.Provider>
  );
};

/**
 * Use modal context
 * @returns {Object} Modal context value
 */
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    // Fallback to store if context not available
    return useUIStore((state) => ({
      open: state.openModal,
      close: state.closeModal,
      closeAll: state.closeAllModals,
      openGeneric: state.openGenericModal,
      closeGeneric: state.closeGenericModal,
      isOpen: (name) => state.modals[name] === true,
    }));
  }
  return context;
};

export default ModalProvider;
